import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { orderForOperationalUse, printUsesConfirmedValues } from '@/utils/offlineEditContinuity';
import { toast } from 'sonner';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { getPublicShopBranding } from '@/api/shop';
import { printOrderSheet } from '@/utils/printOrderSheet';

export const ORDER_SHEET_BRANDING_DEADLINE_MS = 10_000;

// This is the generated sheet's field contract, not a new order/read model.
const PRINTED_FIELDS = [
  'internal_order_number', 'resolved_source', 'source_resolution_status', 'order_number', 'subsource_extracted',
  'delivery_date', 'delivery_time_requirement', 'order_accepted_date', 'recipient_name',
  'recipient_street_address', 'recipient_street_address_2', 'recipient_city',
  'recipient_state', 'recipient_zip_code', 'recipient_phone', 'product_code',
  'product_name', 'size', 'product_description', 'card_message', 'special_instructions',
  'total_price',
];

const capture = (value, fields) => value == null ? null : Object.fromEntries(fields.map((field) => {
  const item = value[field];
  return [field, item == null || ['string', 'number', 'boolean'].includes(typeof item) ? item : String(item)];
}));

/** Own only unfinished viewers. Delivered sheets remain independent snapshots. */
export default function useOrderSheetPrint({ order, canPrint = true, shop, fetchBranding = true, includeOrderNumber = true }) {
  const session = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const latest = useRef(null);
  latest.current = { order, canPrint, shop, fetchBranding, includeOrderNumber, session };
  const mounted = useRef(false);
  const pending = useRef(null);
  const orderId = order?.id;

  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current?.cancel();
    };
  }, [orderId, canPrint, session]);

  return useCallback(() => {
    const input = latest.current;
    if (!mounted.current || !input.order || !input.canPrint
        || input.session !== getActiveSessionRevision()) return;
    if (pending.current) {
      if (pending.current.current()) {
        try { pending.current.popup.focus(); } catch { /* A closed viewer is handled on settlement. */ }
        return;
      }
      pending.current.cancel();
    }

    let captured;
    let capturedShop;
    let popup;
    try {
      captured = capture(orderForOperationalUse(input.order), PRINTED_FIELDS);
      capturedShop = capture(input.shop, ['name', 'address', 'logo_id']);
      // Stay in the click stack. No continuation may open a replacement window.
      popup = window.open('', '_blank', 'width=800,height=1000');
      if (!popup) {
        toast.error('Pop-ups blocked — allow pop-ups to view the order sheet');
        return;
      }
      popup.opener = null;
      if (printUsesConfirmedValues(input.order)) {
        toast.info('Printing confirmed values. Pending offline changes still need review.');
      }
    } catch {
      try { popup?.close(); } catch { /* Best-effort disposal of our unfinished viewer. */ }
      toast.error('Failed to prepare order sheet');
      return;
    }

    const controller = new AbortController();
    const capturedOrderId = input.order.id;
    const operation = { popup, cancelled: false };
    let timer;
    const current = () => pending.current === operation && !operation.cancelled && mounted.current
      && !popup.closed && latest.current.canPrint && latest.current.order?.id === capturedOrderId
      && latest.current.session === input.session && getActiveSessionRevision() === input.session;
    operation.current = current;
    const release = () => {
      clearTimeout(timer);
      popup.removeEventListener?.('pagehide', operation.cancel);
      controller.abort();
      if (pending.current === operation) pending.current = null;
    };
    operation.cancel = () => {
      if (operation.cancelled) return;
      operation.cancelled = true;
      release();
      try { if (!popup.closed) popup.close(); } catch { /* No replacement or retry. */ }
    };
    pending.current = operation;
    const publish = (branding, brandingFallback = false) => {
      if (!current()) { operation.cancel(); return; }
      // document.open can retire the placeholder document; detach its listener first.
      release();
      try {
        printOrderSheet(captured, capture(branding, ['name', 'address', 'logo_id']), {
          popup, brandingFallback, includeOrderNumber: input.includeOrderNumber,
        });
      } catch {
        operation.cancel();
        toast.error('Failed to prepare order sheet');
      }
    };

    if (!input.fetchBranding) {
      publish(capturedShop);
      return;
    }
    try {
      popup.document.open();
      popup.document.write('<!doctype html><html><head><title>Preparing order sheet</title></head><body>Preparing order sheet…</body></html>');
      popup.document.close();
      popup.addEventListener?.('pagehide', operation.cancel);
    } catch {
      operation.cancel();
      toast.error('Failed to prepare order sheet');
      return;
    }
    // No close polling: missed lifecycle events are checked at settlement/deadline.
    timer = setTimeout(() => publish(null, true), ORDER_SHEET_BRANDING_DEADLINE_MS);
    try {
      Promise.resolve(getPublicShopBranding({ signal: controller.signal })).then(
        (branding) => { if (!operation.cancelled && pending.current === operation) publish(branding); },
        () => { if (!operation.cancelled && pending.current === operation) publish(null, true); },
      );
    } catch {
      publish(null, true);
    }
  }, []);
}
